import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { resourcesService } from "../../../services/resourcesService";
import { rolesService } from "../../../services/rolesService";
import { skillsService } from "../../../services/skillsService";
import { categoriesService } from "../../../services/categoriesService";
import { AdminLayout } from "../../layout/AdminLayout";
import { toast } from "react-toastify";
import { X, Plus, Download } from "lucide-react";

export const ResourceForm = () => {
	const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";
	const [resourceId, setResourceId] = useState("");
	const [experience, setExperience] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [contactNumber, setContactNumber] = useState("");
	const [roleId, setRoleId] = useState("");
	const [selectedSkills, setSelectedSkills] = useState([]);
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [selectedSkill, setSelectedSkill] = useState("");
	const [selectedCompetency, setSelectedCompetency] = useState("Beginner");
	const [summary, setSummary] = useState("");
	const [projects, setProjects] = useState([]);
	const [currentCategory, setCurrentCategory] = useState(null);
	const [currentCategoryCompetency, setCurrentCategoryCompetency] =
		useState("Beginner");
	const [currentCategorySkills, setCurrentCategorySkills] = useState([]);
	const [selectedCategorySkill, setSelectedCategorySkill] = useState("");
	const [selectedCategorySkillCompetency, setSelectedCategorySkillCompetency] =
		useState("Beginner");
	const [currentProject, setCurrentProject] = useState({
		project_name: "",
		tools_technologies: "",
		role: "",
		project_summary: "",
		main_responsibilities: "",
	});

	const [roles, setRoles] = useState([]);
	const [skills, setSkills] = useState([]);
	const [generalSkills, setGeneralSkills] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isNew, setIsNew] = useState(true);
	const { id } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		loadData();
	}, []);

	useEffect(() => {
		if (id && id !== "new") {
			setIsNew(false);
			loadResource(id);
		}
	}, [id]);

	const loadData = async () => {
		try {
			const [rolesData, skillData, catData] = await Promise.all([
				rolesService.getAll(1, 1000),
				skillsService.getAll(1, 1000),
				categoriesService.getAll(1, 1000),
			]);
			setRoles(rolesData.data || []);
			setSkills(skillData.data || []);
			setCategories(catData.data || []);

			// Extract skill IDs that are bound to categories
			const boundSkillIds = new Set();
			(catData.data || []).forEach((category) => {
				(category.skills || []).forEach((skill) => {
					const skillId =
						typeof skill === "string" ? skill : skill._id || skill;
					boundSkillIds.add(skillId);
				});
			});

			// Filter skills to only include those not bound to any category
			const general = (skillData.data || []).filter(
				(skill) => !boundSkillIds.has(skill._id),
			);
			setGeneralSkills(general);
		} catch (error) {
			console.log("Error loading form data:", error);
			toast.error("Failed to load form data");
		}
	};

	const loadResource = async (resourceId) => {
		try {
			const data = await resourcesService.getById(resourceId);
			setResourceId(data.resource_id);
			setExperience(data.experience ?? "");
			setFirstName(data.first_name);
			setLastName(data.last_name);
			setEmail(data.email);
			setContactNumber(data.contact_number);
			setSummary(data.summary ?? "");
			if (data.role_id) {
				setRoleId(data.role_id._id);
			}
			if (data.skills) {
				setSelectedSkills(
					data.skills.map((s) => ({
						skill_id:
							typeof s.skill_id === "object"
								? String(s.skill_id._id || s.skill_id)
								: String(s.skill_id),
						competency: s.competency,
					})),
				);
			}
			if (data.categories) {
				setSelectedCategories(
					data.categories.map((c) => {
						const categoryRef = c.category_id;
						const categoryId =
							typeof categoryRef === "object"
								? String(
										categoryRef?._id || categoryRef?.category_id || categoryRef,
									)
								: String(categoryRef);

						const categoryName =
							typeof categoryRef === "object"
								? categoryRef?.name || null
								: null;

						return {
							category_id: categoryId,
							category_name: categoryName,
							competency: c.competency,
							cv_file: c.cv_file || null,
							cv_upload: null,
							skills: (c.skills || []).map((s) => ({
								skill_id:
									typeof s.skill_id === "object"
										? String(s.skill_id._id || s.skill_id)
										: String(s.skill_id),
								skill_name:
									typeof s.skill_id === "object" ? s.skill_id.name : null,
								competency: s.competency,
							})),
						};
					}),
				);
			}
			if (data.projects) {
				setProjects(data.projects);
			}
		} catch (error) {
			console.log("Error loading resource:", error);
			toast.error("Failed to load resource");
			navigate("/admin/resources");
		}
	};

	const addSkill = () => {
		if (!selectedSkill) {
			toast.error("Please select a skill");
			return;
		}

		const alreadyAdded = selectedSkills.some(
			(s) => s.skill_id === selectedSkill,
		);
		if (alreadyAdded) {
			toast.error("Skill already added");
			return;
		}

		setSelectedSkills([
			...selectedSkills,
			{ skill_id: selectedSkill, competency: selectedCompetency },
		]);
		setSelectedSkill("");
		setSelectedCompetency("Beginner");
	};

	const removeSkill = (skillId) => {
		setSelectedSkills(selectedSkills.filter((s) => s.skill_id !== skillId));
	};

	const getCategorySkills = (categoryId) => {
		const category = categories.find((c) => c._id === categoryId);
		return category?.skills || [];
	};

	const addCategorySkill = () => {
		if (!selectedCategorySkill) {
			toast.error("Please select a skill");
			return;
		}

		const alreadyAdded = currentCategorySkills.some(
			(s) => s.skill_id === selectedCategorySkill,
		);
		if (alreadyAdded) {
			toast.error("Skill already added to this category");
			return;
		}

		setCurrentCategorySkills([
			...currentCategorySkills,
			{
				skill_id: selectedCategorySkill,
				competency: selectedCategorySkillCompetency,
			},
		]);
		setSelectedCategorySkill("");
		setSelectedCategorySkillCompetency("Beginner");
	};

	const removeCategorySkill = (skillId) => {
		setCurrentCategorySkills(
			currentCategorySkills.filter((s) => s.skill_id !== skillId),
		);
	};

	const addCategory = () => {
		if (!currentCategory) {
			toast.error("Please select a category");
			return;
		}

		const alreadyAdded = selectedCategories.some(
			(c) => c.category_id === currentCategory,
		);
		if (alreadyAdded) {
			toast.error("Category already added");
			return;
		}

		const categoryName = categories.find(
			(c) => c._id === currentCategory,
		)?.name;

		setSelectedCategories([
			...selectedCategories,
			{
				category_id: currentCategory,
				category_name: categoryName,
				competency: currentCategoryCompetency,
				cv_file: null,
				cv_upload: null,
				skills: currentCategorySkills.map((skill) => ({
					...skill,
					skill_name: skills.find((s) => s._id === skill.skill_id)?.name,
				})),
			},
		]);

		// Reset form
		setCurrentCategory(null);
		setCurrentCategoryCompetency("Beginner");
		setCurrentCategorySkills([]);
		setSelectedCategorySkill("");
		setSelectedCategorySkillCompetency("Beginner");
	};

	const removeCategory = (categoryId) => {
		setSelectedCategories(
			selectedCategories.filter((c) => c.category_id !== categoryId),
		);
	};

	const setCategoryCvUpload = (categoryId, file) => {
		if (file) {
			const maxSize = 2 * 1024 * 1024;
			const isPdfType = file.type === "application/pdf";
			const isPdfName = file.name.toLowerCase().endsWith(".pdf");

			if (!isPdfType || !isPdfName) {
				toast.error("Only PDF files are allowed");
				return;
			}

			if (file.size > maxSize) {
				toast.error("CV file size must be 2MB or less");
				return;
			}
		}

		setSelectedCategories((prev) =>
			prev.map((category) =>
				category.category_id === categoryId
					? { ...category, cv_upload: file || null }
					: category,
			),
		);
	};

	const addProject = () => {
		if (
			!currentProject.project_name ||
			!currentProject.tools_technologies ||
			!currentProject.role ||
			!currentProject.project_summary ||
			!currentProject.main_responsibilities
		) {
			toast.error("Please fill all project fields");
			return;
		}

		setProjects([...projects, { ...currentProject }]);
		setCurrentProject({
			project_name: "",
			tools_technologies: "",
			role: "",
			project_summary: "",
			main_responsibilities: "",
		});
	};

	const removeProject = (index) => {
		setProjects(projects.filter((_, i) => i !== index));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);

		try {
			// Clean up categories for backend - remove display-only fields
			const cleanedCategories = selectedCategories.map((cat) => ({
				category_id: cat.category_id,
				competency: cat.competency,
				skills: cat.skills.map((skill) => ({
					skill_id: skill.skill_id,
					competency: skill.competency,
				})),
			}));

			const categoryCvUploads = selectedCategories.reduce((acc, cat) => {
				if (cat.cv_upload) {
					acc[cat.category_id] = cat.cv_upload;
				}
				return acc;
			}, {});

			const formData = {
				resource_id: Number.parseInt(resourceId),
				experience: Number.parseFloat(experience),
				first_name: firstName,
				last_name: lastName,
				email,
				summary: summary || undefined,
				contact_number: contactNumber,
				role_id: roleId || undefined,
				skills: selectedSkills,
				categories: cleanedCategories,
				projects: projects || [],
				categoryCvUploads,
			};

			if (isNew) {
				await resourcesService.create(formData);
				toast.success("Resource created");
			} else {
				await resourcesService.update(id, formData);
				toast.success("Resource updated");
			}

			navigate("/admin/resources");
		} catch (error) {
			const errorMessage = error.response?.data?.message;
			if (Array.isArray(errorMessage)) {
				// Handle validation errors (array of error messages)
				errorMessage.forEach((msg) => toast.error(msg));
			} else if (errorMessage) {
				toast.error(errorMessage);
			} else {
				toast.error("Failed to save resource");
			}
		} finally {
			setLoading(false);
		}
	};

	const getSkillName = (skillId) => {
		return skills.find((s) => s._id === skillId)?.name || skillId;
	};

	const getFileNameFromPath = (path) => {
		if (!path) {
			return "";
		}
		const segments = String(path).split("/");
		return segments[segments.length - 1] || path;
	};

	const getCvDownloadUrl = (path) => {
		if (!path) {
			return "";
		}
		const normalizedPath = String(path).replace(/^\/+/, "");
		return `${API_URL}/uploads/${normalizedPath}`;
	};

	return (
		<AdminLayout>
			<div className="p-6">
				<div className="max-w-4xl mx-auto">
					<h1 className="text-3xl font-bold text-gray-800 mb-6">
						{isNew ? "Add Resource" : "Edit Resource"}
					</h1>

					<div className="bg-white rounded-lg shadow p-6">
						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Basic Information */}
							<fieldset className="border border-gray-200 rounded-lg p-4">
								<legend className="text-lg font-semibold text-gray-800 px-2">
									Basic Information
								</legend>

								<div className="grid grid-cols-2 gap-4 mt-4">
									<div>
										<label
											htmlFor="resourceId"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Resource ID *
										</label>
										<input
											id="resourceId"
											type="number"
											value={resourceId}
											onChange={(e) => setResourceId(e.target.value)}
											required
											disabled={!isNew}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
											placeholder="e.g., 1001"
										/>
									</div>

									<div>
										<label
											htmlFor="roleId"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Role
										</label>
										<select
											id="roleId"
											value={roleId}
											onChange={(e) => setRoleId(e.target.value)}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										>
											<option value="">Select Role</option>
											{roles.map((role) => (
												<option key={role._id} value={role._id}>
													{role.name}
												</option>
											))}
										</select>
									</div>
									<div>
										<label
											htmlFor="firstName"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											First Name *
										</label>
										<input
											id="firstName"
											type="text"
											value={firstName}
											onChange={(e) => setFirstName(e.target.value)}
											required
											maxLength={255}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											placeholder="John"
										/>
									</div>

									<div>
										<label
											htmlFor="lastName"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Last Name *
										</label>
										<input
											id="lastName"
											type="text"
											value={lastName}
											onChange={(e) => setLastName(e.target.value)}
											required
											maxLength={255}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											placeholder="Doe"
										/>
									</div>
									<div>
										<label
											htmlFor="experience"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Experience (Years) *
										</label>
										<input
											id="experience"
											type="number"
											value={experience}
											onChange={(e) => setExperience(e.target.value)}
											required
											min="0"
											step="0.1"
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											placeholder="e.g., 5"
										/>
									</div>
									<div>
										<label
											htmlFor="email"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Email *
										</label>
										<input
											id="email"
											type="email"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											required
											maxLength={255}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											placeholder="john@example.com"
										/>
									</div>

									<div>
										<label
											htmlFor="contactNumber"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Contact Number *
										</label>
										<input
											id="contactNumber"
											type="tel"
											value={contactNumber}
											onChange={(e) => setContactNumber(e.target.value)}
											required
											maxLength={20}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											placeholder="+1 555 0123"
										/>
									</div>
								</div>
							</fieldset>

							{/* Summary */}
							<fieldset className="border border-gray-200 rounded-lg p-4">
								<legend className="text-lg font-semibold text-gray-800 px-2">
									Summary
								</legend>

								<div className="mt-4">
									<label
										htmlFor="summary"
										className="block text-sm font-medium text-gray-700 mb-2"
									>
										Professional Summary
									</label>
									<textarea
										id="summary"
										value={summary}
										onChange={(e) => setSummary(e.target.value)}
										maxLength={1000}
										rows={4}
										className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
										placeholder="Brief professional summary or biography..."
									/>
									<p className="text-xs text-gray-500 mt-1">
										{summary.length}/1000 characters
									</p>
								</div>
							</fieldset>

							{/* Skills */}
							<fieldset className="border border-gray-200 rounded-lg p-4">
								<legend className="text-lg font-semibold text-gray-800 px-2">
									Skills
								</legend>

								<div className="mt-4 space-y-4">
									<div className="flex gap-2">
										<select
											value={selectedSkill}
											onChange={(e) => setSelectedSkill(e.target.value)}
											className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										>
											<option value="">Select General Skill</option>
											{generalSkills.map((skill) => (
												<option key={skill._id} value={skill._id}>
													{skill.name}
												</option>
											))}
										</select>

										<select
											value={selectedCompetency}
											onChange={(e) => setSelectedCompetency(e.target.value)}
											className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										>
											<option value="Beginner">Beginner</option>
											<option value="Intermediate">Intermediate</option>
											<option value="Expert">Expert</option>
										</select>

										<button
											type="button"
											onClick={addSkill}
											className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition flex items-center gap-2"
										>
											<Plus size={20} />
											Add
										</button>
									</div>

									{selectedSkills.length > 0 && (
										<div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
											{selectedSkills.map((skill) => (
												<div
													key={skill.skill_id}
													className="flex justify-between items-center bg-white p-3 rounded border border-gray-200 h-full"
												>
													<div>
														<p className="font-medium text-gray-800">
															{getSkillName(skill.skill_id)}
														</p>
														<p className="text-sm text-gray-600">
															{skill.competency}
														</p>
													</div>
													<button
														type="button"
														onClick={() => removeSkill(skill.skill_id)}
														className="text-red-600 hover:text-red-800"
													>
														<X size={20} />
													</button>
												</div>
											))}
										</div>
									)}
								</div>
							</fieldset>

							{/* Categories */}
							<fieldset className="border border-gray-200 rounded-lg p-4">
								<legend className="text-lg font-semibold text-gray-800 px-2">
									Categories
								</legend>

								<div className="mt-4 space-y-4">
									{/* Add Category Form */}
									<div className="bg-gray-50 rounded-lg p-4 space-y-3">
										<div>
											<label
												htmlFor="categorySelect"
												className="block text-sm font-medium text-gray-700 mb-2"
											>
												Select Category
											</label>
											<select
												id="categorySelect"
												value={currentCategory || ""}
												onChange={(e) => {
													setCurrentCategory(e.target.value);
													setCurrentCategorySkills([]);
													setSelectedCategorySkill("");
													setSelectedCategorySkillCompetency("Beginner");
												}}
												className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											>
												<option value="">Choose a category...</option>
												{categories
													.filter(
														(cat) =>
															!selectedCategories.some(
																(sc) => sc.category_id === cat._id,
															),
													)
													.map((category) => (
														<option key={category._id} value={category._id}>
															{category.name}
														</option>
													))}
											</select>
										</div>

										{currentCategory && (
											<>
												<div>
													<label
														htmlFor="categoryCompetency"
														className="block text-sm font-medium text-gray-700 mb-2"
													>
														Category Competency Level
													</label>
													<select
														id="categoryCompetency"
														value={currentCategoryCompetency}
														onChange={(e) =>
															setCurrentCategoryCompetency(e.target.value)
														}
														className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
													>
														<option value="Beginner">Beginner</option>
														<option value="Intermediate">Intermediate</option>
														<option value="Expert">Expert</option>
													</select>
												</div>

												{/* Category Skills Form */}
												<div className="border-t pt-3">
													<p className="text-sm font-medium text-gray-700 mb-3">
														Add Skills to{" "}
														{categories.find((c) => c._id === currentCategory)
															?.name || "this category"}
													</p>

													<div className="flex gap-2 mb-3">
														<select
															value={selectedCategorySkill}
															onChange={(e) =>
																setSelectedCategorySkill(e.target.value)
															}
															className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
														>
															<option value="">Select skill...</option>
															{(getCategorySkills(currentCategory) || [])
																.filter(
																	(skill) =>
																		!currentCategorySkills.some(
																			(cs) =>
																				cs.skill_id ===
																				(typeof skill === "string"
																					? skill
																					: skill._id),
																		),
																)
																.map((skill) => {
																	const skillId =
																		typeof skill === "string"
																			? skill
																			: skill._id;
																	const skillObj =
																		skills.find((s) => s._id === skillId) || {};
																	return (
																		<option key={skillId} value={skillId}>
																			{skillObj.name || skillId}
																		</option>
																	);
																})}
														</select>

														<select
															value={selectedCategorySkillCompetency}
															onChange={(e) =>
																setSelectedCategorySkillCompetency(
																	e.target.value,
																)
															}
															className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
														>
															<option value="Beginner">Beginner</option>
															<option value="Intermediate">Intermediate</option>
															<option value="Expert">Expert</option>
														</select>

														<button
															type="button"
															onClick={addCategorySkill}
															className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition flex items-center gap-1 text-sm"
														>
															<Plus size={16} />
															Add
														</button>
													</div>

													{/* Listed Category Skills */}
													{currentCategorySkills.length > 0 && (
														<div className="bg-white border border-gray-200 rounded-lg p-2 space-y-2 mb-3">
															{currentCategorySkills.map((skill) => (
																<div
																	key={skill.skill_id}
																	className="flex justify-between items-center bg-gray-50 p-2 rounded text-sm"
																>
																	<div>
																		<p className="font-medium text-gray-800">
																			{skills.find(
																				(s) => s._id === skill.skill_id,
																			)?.name || skill.skill_id}
																		</p>
																		<p className="text-xs text-gray-600">
																			{skill.competency}
																		</p>
																	</div>
																	<button
																		type="button"
																		onClick={() =>
																			removeCategorySkill(skill.skill_id)
																		}
																		className="text-red-600 hover:text-red-800"
																	>
																		<X size={16} />
																	</button>
																</div>
															))}
														</div>
													)}
												</div>

												<button
													type="button"
													onClick={addCategory}
													className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition flex items-center justify-center gap-2"
												>
													<Plus size={20} />
													Add Category
												</button>
											</>
										)}
									</div>

									{/* Listed Categories */}
									{selectedCategories.length > 0 && (
										<div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
											{selectedCategories.map((category) => (
												<div
													key={category.category_id}
													className="border border-gray-200 rounded-lg p-3 space-y-2 h-full"
												>
													<div className="flex justify-between items-start">
														<div className="flex-1">
															<p className="font-semibold text-gray-800">
																{category.category_name ||
																	categories.find(
																		(c) => c._id === category.category_id,
																	)?.name ||
																	"Category"}
															</p>
															<p className="text-sm text-gray-600">
																{category.competency}
															</p>
															<div className="mt-2">
																<label className="block text-xs font-medium text-gray-700 mb-1">
																	Category CV
																</label>
																<input
																	type="file"
																	accept=".pdf,application/pdf"
																	onChange={(e) =>
																		setCategoryCvUpload(
																			category.category_id,
																			e.target.files?.[0] || null,
																		)
																	}
																	className="text-xs text-gray-700"
																/>
																<p className="text-xs text-gray-500 mt-1">
																	PDF only, max 2MB
																</p>
																{category.cv_upload && (
																	<p className="text-xs text-green-700 mt-1">
																		Selected: {category.cv_upload.name}
																	</p>
																)}
																{!category.cv_upload && category.cv_file && (
																	<div className="flex items-center gap-2 mt-1">
																		<p className="text-xs text-gray-500">
																			Current:{" "}
																			{getFileNameFromPath(category.cv_file)}
																		</p>
																		<a
																			href={getCvDownloadUrl(category.cv_file)}
																			target="_blank"
																			rel="noreferrer"
																			download={getFileNameFromPath(
																				category.cv_file,
																			)}
																			className="inline-flex items-center justify-center text-blue-600 hover:text-blue-800"
																			title="Download CV"
																		>
																			<Download size={14} />
																		</a>
																	</div>
																)}
															</div>
															{category.skills &&
																category.skills.length > 0 && (
																	<div className="mt-2 space-y-1">
																		<p className="text-xs font-medium text-gray-700">
																			Skills:
																		</p>
																		{category.skills.map((skill) => (
																			<div
																				key={skill.skill_id}
																				className="text-xs text-gray-600"
																			>
																				•{" "}
																				{skill.skill_name ||
																					skills.find(
																						(s) => s._id === skill.skill_id,
																					)?.name ||
																					skill.skill_id}
																				: {skill.competency}
																			</div>
																		))}
																	</div>
																)}
														</div>
														<button
															type="button"
															onClick={() =>
																removeCategory(category.category_id)
															}
															className="text-red-600 hover:text-red-800 ml-2"
														>
															<X size={20} />
														</button>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</fieldset>

							{/* Projects */}
							<fieldset className="border border-gray-200 rounded-lg p-4">
								<legend className="text-lg font-semibold text-gray-800 px-2">
									Projects
								</legend>

								<div className="mt-4 space-y-4">
									{/* Add Project Form */}
									<div className="bg-gray-50 rounded-lg p-4 space-y-3">
										<div>
											<label
												htmlFor="projectName"
												className="block text-sm font-medium text-gray-700 mb-1"
											>
												Project Name
											</label>
											<input
												id="projectName"
												type="text"
												value={currentProject.project_name}
												onChange={(e) =>
													setCurrentProject({
														...currentProject,
														project_name: e.target.value,
													})
												}
												maxLength={255}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
												placeholder="e.g., E-commerce Platform"
											/>
										</div>

										<div>
											<label
												htmlFor="toolsTechnologies"
												className="block text-sm font-medium text-gray-700 mb-1"
											>
												Tools & Technologies
											</label>
											<input
												id="toolsTechnologies"
												type="text"
												value={currentProject.tools_technologies}
												onChange={(e) =>
													setCurrentProject({
														...currentProject,
														tools_technologies: e.target.value,
													})
												}
												maxLength={500}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
												placeholder="e.g., React, Node.js, MongoDB"
											/>
										</div>

										<div>
											<label
												htmlFor="projectRole"
												className="block text-sm font-medium text-gray-700 mb-1"
											>
												Role
											</label>
											<input
												id="projectRole"
												type="text"
												value={currentProject.role}
												onChange={(e) =>
													setCurrentProject({
														...currentProject,
														role: e.target.value,
													})
												}
												maxLength={255}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
												placeholder="e.g., Full Stack Developer"
											/>
										</div>

										<div>
											<label
												htmlFor="projectSummary"
												className="block text-sm font-medium text-gray-700 mb-1"
											>
												Project Summary
											</label>
											<textarea
												id="projectSummary"
												value={currentProject.project_summary}
												onChange={(e) =>
													setCurrentProject({
														...currentProject,
														project_summary: e.target.value,
													})
												}
												maxLength={500}
												rows={2}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
												placeholder="Brief overview of the project..."
											/>
											<p className="text-xs text-gray-500 mt-1">
												{currentProject.project_summary.length}/500 characters
											</p>
										</div>

										<div>
											<label
												htmlFor="mainResponsibilities"
												className="block text-sm font-medium text-gray-700 mb-1"
											>
												Main Responsibilities
											</label>
											<textarea
												id="mainResponsibilities"
												value={currentProject.main_responsibilities}
												onChange={(e) =>
													setCurrentProject({
														...currentProject,
														main_responsibilities: e.target.value,
													})
												}
												maxLength={1000}
												rows={3}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
												placeholder="Describe your main responsibilities in this project..."
											/>
											<p className="text-xs text-gray-500 mt-1">
												{currentProject.main_responsibilities.length}/1000
												characters
											</p>
										</div>

										<button
											type="button"
											onClick={addProject}
											className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition flex items-center justify-center gap-2"
										>
											<Plus size={20} />
											Add Project
										</button>
									</div>

									{/* Listed Projects */}
									{projects.length > 0 && (
										<div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
											{projects.map((project, index) => (
												<div
													key={index}
													className="border border-gray-200 rounded-lg p-3 space-y-2 h-full"
												>
													<div className="flex justify-between items-start">
														<div className="flex-1">
															<p className="font-semibold text-gray-800">
																{project.project_name}
															</p>
															<p className="text-sm text-gray-600">
																{project.role}
															</p>
															<p className="text-xs text-gray-500 mt-1">
																{project.tools_technologies}
															</p>
															<p className="text-sm text-gray-700 mt-2">
																{project.project_summary}
															</p>
															<p className="text-xs text-gray-600 mt-2">
																<span className="font-medium">
																	Responsibilities:
																</span>{" "}
																{project.main_responsibilities}
															</p>
														</div>
														<button
															type="button"
															onClick={() => removeProject(index)}
															className="text-red-600 hover:text-red-800 ml-2"
														>
															<X size={20} />
														</button>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</fieldset>

							<div className="flex gap-2">
								<button
									type="submit"
									disabled={loading}
									className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition"
								>
									{loading ? "Saving..." : "Save Resource"}
								</button>
								<button
									type="button"
									onClick={() => navigate("/admin/resources")}
									className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 rounded-lg transition"
								>
									Cancel
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</AdminLayout>
	);
};
